import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' 
  : 'http://localhost:3000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const songAPI = {
  // Get all songs with optional search and sorting
  getSongs: async (search = '', sortBy = 'name', sortOrder = 'ASC') => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder) params.append('sortOrder', sortOrder);
    
    const response = await api.get(`/api/songs?${params.toString()}`);
    return response.data;
  },

  // Get specific song by ID
  getSong: async (id) => {
    const response = await api.get(`/api/songs/${id}`);
    return response.data;
  },

  // Create new song
  createSong: async (songData) => {
    const response = await api.post('/api/songs', songData);
    return response.data;
  },

  // Update song
  updateSong: async (id, songData) => {
    const response = await api.put(`/api/songs/${id}`, songData);
    return response.data;
  },

  // Adjust current amount (+1 or -1)
  adjustAmount: async (id, adjustment) => {
    const response = await api.patch(`/api/songs/${id}/adjust`, { adjustment });
    return response.data;
  },

  // Delete song
  deleteSong: async (id) => {
    const response = await api.delete(`/api/songs/${id}`);
    return response.data;
  },

  // Get statistics
  getStats: async () => {
    const response = await api.get('/api/stats');
    return response.data;
  },

  // Generate QR PDF for song
  generateQRPDF: async (id) => {
    const response = await api.get(`/api/songs/${id}/qr-pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Confirm count for song
  confirmCount: async (id) => {
    const response = await api.post(`/api/songs/${id}/confirm-count`);
    return response.data;
  },

  // Confirm amount in folder
  confirmAmountInFolder: async (id) => {
    const response = await api.post(`/api/songs/${id}/confirm-amount-folder`);
    return response.data;
  },

  // Get history for a specific song
  getSongHistory: async (id) => {
    const response = await api.get(`/api/songs/${id}/history`);
    return response.data;
  },

  // Get all history
  getAllHistory: async (limit = 100) => {
    const response = await api.get(`/api/history?limit=${limit}`);
    return response.data;
  },

  // Upload PDF for song
  uploadPDF: async (id, file) => {
    const formData = new FormData();
    formData.append('pdf', file);
    
    const response = await api.post(`/api/songs/${id}/upload-pdf`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get PDF for song
  getPDF: async (id) => {
    const response = await api.get(`/api/songs/${id}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Delete PDF for song
  deletePDF: async (id) => {
    const response = await api.delete(`/api/songs/${id}/pdf`);
    return response.data;
  },

  // Get merged QR PDF for multiple songs
  getMergedQRPDF: async (songIds) => {
    const response = await api.post('/api/qr-codes/merged-pdf', { songIds }, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Confirm QR pages printed for multiple songs
  confirmQRPagesPrinted: async (songIds) => {
    const response = await api.post('/api/qr-codes/confirm-printed', { songIds });
    return response.data;
  },

  // --- Groups ---
  getGroups: async () => {
    const response = await api.get('/api/groups');
    return response.data;
  },

  getGroup: async (id) => {
    const response = await api.get(`/api/groups/${id}`);
    return response.data;
  },

  getSharedGroup: async (slug) => {
    const response = await api.get(`/api/groups/share/${slug}`);
    return response.data;
  },

  createGroup: async (name) => {
    const response = await api.post('/api/groups', { name });
    return response.data;
  },

  updateGroup: async (id, data) => {
    const response = await api.put(`/api/groups/${id}`, data);
    return response.data;
  },

  deleteGroup: async (id) => {
    const response = await api.delete(`/api/groups/${id}`);
    return response.data;
  }
};

export default api;

